import { segmentOrder } from './findSegment';

/**
 * A streamed search's state, built from its run's events (`followFindRun`): the tile plan,
 * each tile's progress and the results so far, keyed by id so a VLM verdict restyles its box in
 * place. `null` before the first search.
 */

export const TILE_STATE = {
	QUEUED: 'queued',
	RUNNING: 'running',
	DONE: 'done',
	CHECKED: 'checked',
};

const toResult = (feature, fallbackId, preview = false) => ({
	id: feature.properties?.id ?? fallbackId,
	geometry: feature.geometry,
	bbox: feature.properties?.bbox ?? null,
	label: feature.properties?.label ?? 'object',
	// Which object of the search it is ("car" 0, "house" 1), for its colour
	group: feature.properties?.group ?? 0,
	score: Number(feature.properties?.score) || 0,
	sources: feature.properties?.sources ?? [],
	kept: feature.properties?.kept ?? null,
	// The grid square (plan tile index) whose cell holds the box, for reviewing square by square
	tile: feature.properties?.tile ?? null,
	// Unfused boxes of "find the rest", shown until the fused candidates replace them
	preview,
	// Segment mode: a traced area or centreline (no bbox), its size, and whether it bridges a gap
	kind: feature.properties?.kind ?? null,
	inferred: Boolean(feature.properties?.inferred),
	length_m: feature.properties?.length_m ?? null,
	area_m2: feature.properties?.area_m2 ?? null,
});

// A run is a segment run once its plan (or end) says so; a query has no other way to know
const segmentKind = (state, event) => (event.mode === 'segment' ? 'segment' : state.kind);

const keyed = results => Object.fromEntries(results.map(result => [result.id, result]));

// Boxes the user added (a click on the heatmap, "Add missed"): the search's own results replace each
// other as it goes, these stay
const ADDED_SOURCES = ['manual', 'click'];
const added = state =>
	keyed(
		Object.values(state?.byId ?? {}).filter(result =>
			result.sources.some(source => ADDED_SOURCES.includes(source))
		)
	);

const fresh = kind => ({
	kind,
	running: true,
	// Asked to stop: it goes on to the end of its square, then says `stopped`
	stopping: false,
	stopped: false,
	error: null,
	plan: null,
	tiles: {},
	byId: {},
	name: null,
	// The objects searched for, in `group` order ("car", "house"), once the search is done
	labels: null,
	namedBy: null,
	verified: false,
	// What the run cost ({ usd_total, usd_compute, usd_llm, usd_startup }), once the server says
	cost: null,
	// A segment run's totals ({ length_km, area_ha, pieces, gaps_bridged }), once done
	stats: null,
	// The job the results were saved as ({ job_uuid, count, labels, saved_at }), once saved
	saved: null,
	startedAt: Date.now(),
	finishedAt: null,
});

const withTile = (state, i, change) => ({
	...state,
	tiles: { ...state.tiles, [i]: { ...state.tiles[i], ...change } },
});

// A finished square: its state, and the boxes it found
const tileChange = event => ({
	state: TILE_STATE.DONE,
	count: event.features.length,
	ms: event.ms,
});
const tileFound = event =>
	event.features.map((f, k) => toResult(f, `t-${event.i}-${k}`, Boolean(event.preview)));

// A run's past in one go (a page picking it up): squares and their boxes gathered into one copy each,
// as one event at a time would copy all of them for every square
const replay = (state, events) => {
	let next = state;
	let tiles = null;
	let byId = null;
	const settle = () => {
		if (tiles) next = { ...next, tiles };
		if (byId) next = { ...next, byId };
		tiles = null;
		byId = null;
	};
	events.forEach(event => {
		if (event.type === 'tile_start' || event.type === 'tile') {
			tiles ??= { ...next.tiles };
			const change =
				event.type === 'tile' ? tileChange(event) : { state: TILE_STATE.RUNNING };
			tiles[event.i] = { ...tiles[event.i], ...change };
			if (event.type === 'tile') {
				byId ??= { ...next.byId };
				tileFound(event).forEach(result => {
					byId[result.id] = result;
				});
			}
			return;
		}
		settle();
		next = searchReducer(next, event);
	});
	settle();
	return next;
};

export const searchReducer = (state, event) => {
	// `request`: what the search was started with, to show it again when a page picks the run up;
	// `cost` and `saved`: a picked-up run's cost so far and the job it was saved as
	if (event.type === 'start')
		return {
			...fresh(event.kind),
			request: event.request ?? null,
			cost: event.cost ?? null,
			saved: event.saved ?? null,
		};
	// A box added before any search (a click on the heatmap): the start of a list of picked objects
	if (event.type === 'add' && !state) {
		const result = toResult(event.feature, event.feature.properties.id);
		return {
			...fresh('picked'),
			running: false,
			labels: [result.label],
			byId: { [result.id]: result },
			finishedAt: Date.now(),
		};
	}
	if (event.type === 'reset' || !state) return null;

	switch (event.type) {
		case 'plan':
			return {
				...state,
				// Words naming only continuous things (roads, rivers) are traced: a segment run
				kind: segmentKind(state, event),
				plan: event,
				tiles: Object.fromEntries(
					event.tiles.map(tile => [tile.i, { state: TILE_STATE.QUEUED, count: 0 }])
				),
			};
		case 'named':
			return { ...state, name: event.name, namedBy: event.named_by };
		case 'preview':
			return {
				...state,
				byId: {
					...state.byId,
					...keyed(
						event.features.map((f, k) => toResult(f, `p-${event.source}-${k}`, true))
					),
				},
			};
		case 'tile_start':
			return withTile(state, event.i, { state: TILE_STATE.RUNNING });
		case 'tile': {
			const next = withTile(state, event.i, tileChange(event));
			return { ...next, byId: { ...next.byId, ...keyed(tileFound(event)) } };
		}
		case 'batch':
			return replay(state, event.events);
		case 'fused':
			return {
				...state,
				byId: {
					...keyed(event.features.map((f, k) => toResult(f, `f-${k}`))),
					...added(state),
				},
			};
		case 'verified': {
			// A check that failed (no answer from the VLM) leaves the square unchecked, not rejected
			if (event.ok === false) return state;
			const byId = { ...state.byId };
			Object.entries(event.kept).forEach(([id, kept]) => {
				if (byId[id]) byId[id] = { ...byId[id], kept };
			});
			const kept = Object.values(event.kept).filter(Boolean).length;
			return { ...withTile(state, event.i, { state: TILE_STATE.CHECKED, kept }), byId };
		}
		case 'done': {
			const kind = segmentKind(state, event);
			const found = event.features.map((f, k) => toResult(f, `d-${k}`));
			// Traced pieces draw areas under their centrelines; boxes best first
			const results =
				kind === 'segment' ? segmentOrder(found) : found.sort((a, b) => b.score - a.score);
			const tiles = Object.fromEntries(
				Object.entries(state.tiles).map(([i, tile]) => [
					i,
					// "find the rest" without a name runs no tile; its tiles are covered all the same
					tile.state === TILE_STATE.QUEUED ? { ...tile, state: TILE_STATE.DONE } : tile,
				])
			);
			return {
				...state,
				kind,
				running: false,
				stopping: false,
				tiles,
				byId: { ...keyed(results), ...added(state) },
				stats: event.stats ?? state.stats,
				verified: event.verified,
				name: event.name ?? state.name,
				namedBy: event.named_by ?? state.namedBy,
				labels: event.labels ?? state.labels,
				cost: event.cost ?? state.cost,
				finishedAt: Date.now(),
			};
		}
		case 'error':
			return {
				...state,
				running: false,
				stopping: false,
				error: event.message,
				finishedAt: Date.now(),
			};
		case 'stopping':
			return state.running ? { ...state, stopping: true } : state;
		// A box the search missed, added by hand ("Add missed")
		case 'add': {
			const result = toResult(event.feature, event.feature.properties.id);
			return { ...state, byId: { ...state.byId, [result.id]: result } };
		}
		// Stopped by the user: what the finished squares found stays, the rest is not searched
		case 'stopped': {
			const tiles = Object.fromEntries(
				Object.entries(state.tiles).map(([i, tile]) => [
					i,
					tile.state === TILE_STATE.RUNNING
						? { ...tile, state: TILE_STATE.QUEUED }
						: tile,
				])
			);
			return {
				...state,
				running: false,
				stopping: false,
				stopped: true,
				tiles,
				cost: event.cost ?? state.cost,
				finishedAt: Date.now(),
			};
		}
		// What the run's document says once it ended: its cost, and the job it was saved as
		case 'meta':
			return {
				...state,
				cost: event.cost ?? state.cost,
				saved: event.saved ?? state.saved,
			};
		// The results saved to the project as a job
		case 'saved':
			return { ...state, saved: event.saved, cost: event.saved?.cost ?? state.cost };
		// Keep-alives (`ping`) and anything new change nothing
		default:
			return state;
	}
};

/** Counts for the progress line: tiles done of total, the tile running, results, tiles checked. */
export const searchProgress = search => {
	if (!search?.plan) return null;
	const tiles = Object.values(search.tiles);
	const done = tiles.filter(
		tile => tile.state === TILE_STATE.DONE || tile.state === TILE_STATE.CHECKED
	);
	const timed = done.filter(tile => tile.ms);
	return {
		total: tiles.length,
		done: done.length,
		checked: tiles.filter(tile => tile.state === TILE_STATE.CHECKED).length,
		found: Object.values(search.byId).filter(result => !result.preview).length,
		msPerTile: timed.length
			? Math.round(timed.reduce((sum, tile) => sum + tile.ms, 0) / timed.length)
			: null,
	};
};
